import { useState, useCallback, useEffect, useRef, lazy, Suspense, type ReactNode } from 'react'
import { SyncStatusIndicator } from './components/SyncStatusIndicator'
import { CookieConsent } from './components/CookieConsent'
import { Screen1 } from './screens/Screen1'

const Screen2 = lazy(() => import('./screens/Screen2').then(m => ({ default: m.Screen2 })))
const Screen3 = lazy(() => import('./screens/Screen3').then(m => ({ default: m.Screen3 })))
const Screen4 = lazy(() => import('./screens/Screen4').then(m => ({ default: m.Screen4 })))
const Screen5 = lazy(() => import('./screens/Screen5').then(m => ({ default: m.Screen5 })))
const Screen6 = lazy(() => import('./screens/Screen6').then(m => ({ default: m.Screen6 })))
const ScreenDailyProgress = lazy(() => import('./screens/ScreenDailyProgress').then(m => ({ default: m.ScreenDailyProgress })))
const ScreenCalendar = lazy(() => import('./screens/ScreenCalendar').then(m => ({ default: m.ScreenCalendar })))
const ScreenLogin = lazy(() => import('./screens/ScreenLogin').then(m => ({ default: m.ScreenLogin })))
const ScreenSignUp = lazy(() => import('./screens/ScreenSignUp').then(m => ({ default: m.ScreenSignUp })))
const ScreenVerifyEmail = lazy(() => import('./screens/ScreenVerifyEmail').then(m => ({ default: m.ScreenVerifyEmail })))
const ScreenForgotPassword = lazy(() => import('./screens/ScreenForgotPassword').then(m => ({ default: m.ScreenForgotPassword })))
const ScreenResetPassword = lazy(() => import('./screens/ScreenResetPassword').then(m => ({ default: m.ScreenResetPassword })))
const ScreenChildList = lazy(() => import('./screens/ScreenChildList').then(m => ({ default: m.ScreenChildList })))
const Screen404 = lazy(() => import('./screens/Screen404').then(m => ({ default: m.Screen404 })))
const Screen500 = lazy(() => import('./screens/Screen500').then(m => ({ default: m.Screen500 })))
const RecipesScreen = lazy(() => import('./screens/RecipesScreen').then(m => ({ default: m.RecipesScreen })))
const ScreenAccountSettings = lazy(() => import('./screens/ScreenAccountSettings').then(m => ({ default: m.ScreenAccountSettings })))
import {
  getStoredChildren,
  addStoredChild,
  updateStoredChild,
  getActiveChildId,
  setActiveChildId,
} from './data/childrenStorage'
import { getStoredProfile, clearLegacyProfile } from './data/profileStorage'
import { getSessionsForDate, setSessionsForDate } from './data/sessionStorage'
import { syncChild, syncAllChildren, loadAllUserData, syncRecipe, deleteRecipeSync } from './services/syncService'
import { getAuthSession, onAuthStateChange, authSignOut } from './lib/supabaseClient'
import type { ChildProfile, TrainingSession, SavedRecipe } from './types'
import { createEmptySession } from './types'
import { useRealtimeSync, type RemoteUpdateEvent } from './hooks/useRealtimeSync'
import { getTodayDateKey, pruneOldLocalData, getSavedRecipes, saveRecipe, updateRecipe, deleteRecipe, updateRecipeTags } from './data/mealLogStorage'
import { backfillScannedProductsCache } from './data/scannedProductsCache'

// ── Screen persistence ────────────────────────────────────────────────────

const SCREEN_RESTORE_KEY = 'energymatch-last-screen'
const SCREEN_RESTORE_TTL = 24 * 60 * 60 * 1000 // 24 hours (covers the 8-hour idle timeout window)

const RESTORABLE_STEPS = new Set([
  'training', 'fuel-plan', 'meals', 'feedback', 'daily-progress', 'calendar', 'recipes',
])

function saveLastScreen(step: string, dateKey: string) {
  if (!RESTORABLE_STEPS.has(step)) return
  localStorage.setItem(SCREEN_RESTORE_KEY, JSON.stringify({ step, dateKey, ts: Date.now() }))
}

function loadLastScreen(): { step: string; dateKey: string } | null {
  try {
    const raw = localStorage.getItem(SCREEN_RESTORE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as { step: string; dateKey: string; ts: number }
    if (Date.now() - data.ts > SCREEN_RESTORE_TTL) {
      localStorage.removeItem(SCREEN_RESTORE_KEY)
      return null
    }
    if (!RESTORABLE_STEPS.has(data.step)) return null
    return data
  } catch {
    return null
  }
}

// ── App steps ─────────────────────────────────────────────────────────────

type AppStep =
  | 'loading'
  | 'landing'
  | 'login'
  | 'forgot-password'
  | 'reset-password'
  | 'signup'
  | 'verify-email'
  | 'child-list'
  | 'add-child'
  | 'edit-child'
  | 'training'
  | 'fuel-plan'
  | 'meals'
  | 'feedback'
  | 'daily-progress'
  | 'calendar'
  | 'account-settings'
  | 'recipes'
  | 'not-found'
  | 'server-error'

// Synchronous check: if no stored children and no Supabase session token in
// localStorage, we can safely show the landing page without waiting for auth.
function getInitialStep(): AppStep {
  const storedChildren = getStoredChildren()
  const hasSupabaseSession = Object.keys(localStorage).some(
    k => k.startsWith('sb-') && k.includes('-auth-token')
  )
  if (storedChildren.length === 0 && !hasSupabaseSession) {
    return 'landing'
  }
  return 'loading'
}

export default function App() {
  const [step, setStep] = useState<AppStep>(getInitialStep)
  const [userId, setUserId] = useState<string | null>(null)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [authToken, setAuthToken] = useState<string>('')
  const [children, setChildren] = useState<ChildProfile[]>([])
  const [activeChild, setActiveChildState] = useState<ChildProfile | null>(null)
  const [editingChild, setEditingChild] = useState<ChildProfile | null>(null)
  const [verifyEmail, setVerifyEmail] = useState('')
  const [guestDataToImport, setGuestDataToImport] = useState<ChildProfile[] | null>(null)
  const [cloudDataError, setCloudDataError] = useState(false)

  const [selectedDateKey, setSelectedDateKey] = useState<string>(() => getTodayDateKey())
  const [sessions, setSessionsState] = useState<TrainingSession[]>([])
  const [logFocusSlotId, setLogFocusSlotId] = useState<string | null>(null)
  const [logPrefill, setLogPrefill] = useState<{ slotId: string; mealName: string } | null>(null)
  const [pendingRecipe, setPendingRecipe] = useState<SavedRecipe | null>(null)
  const [showNewDayToast, setShowNewDayToast] = useState(false)

  // Refs for use inside stable event handlers (avoids stale closures)
  const activeChildRef = useRef<ChildProfile | null>(null)
  const selectedDateKeyRef = useRef<string>(getTodayDateKey())

  // ── Auth init ────────────────────────────────────────────────────────────

  // Supabase fires SIGNED_IN before PASSWORD_RECOVERY on reset links.
  // Check both hash (implicit flow) and search params (PKCE flow, our ?reset=true marker).
  const isPasswordRecovery = useRef(
    window.location.hash.includes('type=recovery') ||
    window.location.search.includes('reset=true')
  )

  // Guard against re-routing on Supabase token refresh events.
  // Supabase fires SIGNED_IN on every token refresh (e.g. when returning to the tab),
  // not just on actual login. We only want to run routing logic once per session.
  const hasRoutedRef = useRef(false)

  useEffect(() => { backfillScannedProductsCache() }, [])

  useEffect(() => {
    // Subscribe to auth state changes (handles email-link redirects automatically)
    // IMPORTANT: This callback must NOT be async and must NOT await anything.
    // Supabase's _notifyAllSubscribers awaits each callback, which would block
    // initializePromise and prevent getAuthSession().then() from resolving.
    const unsubscribe = onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const uid = session.user.id
        const email = session.user.email ?? null
        setUserId(uid)
        setUserEmail(email)
        setAuthToken(session.access_token ?? '')

        // Skip re-routing on token refresh / tab-focus session restores.
        if (hasRoutedRef.current) return
        hasRoutedRef.current = true

        if (isPasswordRecovery.current) {
          setStep('reset-password')
          return
        }

        const preExistingChildren = getStoredChildren()

        if (preExistingChildren.length > 0) {
          // Cached data — route immediately, sync cloud in background
          setChildren(preExistingChildren)
          const lastScreen = loadLastScreen() as { step: AppStep; dateKey: string } | null ?? undefined
          if (preExistingChildren.length === 1) {
            activateChild(preExistingChildren[0], uid, lastScreen)
          } else {
            const savedActiveId = getActiveChildId()
            const savedActive = savedActiveId
              ? preExistingChildren.find((c) => c.id === savedActiveId)
              : null
            if (savedActive) {
              activateChild(savedActive, uid, lastScreen)
            } else {
              setStep('child-list')
            }
          }
          loadAllUserData(uid).then(({ error }) => {
            const refreshed = getStoredChildren()
            if (refreshed.length > 0) setChildren(refreshed)
            if (error && refreshed.length === 0) setCloudDataError(true)
          })
        } else {
          // No local data — fire-and-forget cloud sync; loading timeout handles escape
          loadAllUserData(uid).then(({ found: foundCloudData, error: cloudError }) => {
            const storedChildren = getStoredChildren()
            setChildren(storedChildren)
            setCloudDataError(cloudError && storedChildren.length === 0)
            const legacyProfile = getStoredProfile()
            if (legacyProfile) {
              const asChild: ChildProfile = { id: crypto.randomUUID(), ...legacyProfile }
              setGuestDataToImport([asChild])
              setStep((s) => s === 'loading' ? 'child-list' : s)
            } else if (!foundCloudData) {
              setStep((s) => s === 'loading' ? 'add-child' : s)
            } else if (storedChildren.length === 0) {
              setStep((s) => s === 'loading' ? 'add-child' : s)
            } else if (storedChildren.length === 1) {
              activateChild(storedChildren[0], uid, loadLastScreen() as { step: AppStep; dateKey: string } | null ?? undefined)
            } else {
              const savedActiveId = getActiveChildId()
              const savedActive = savedActiveId
                ? storedChildren.find((c) => c.id === savedActiveId)
                : null
              if (savedActive) {
                activateChild(savedActive, uid, loadLastScreen() as { step: AppStep; dateKey: string } | null ?? undefined)
              } else {
                setStep((s) => s === 'loading' ? 'child-list' : s)
              }
            }
          })
        }
      } else if (event === 'PASSWORD_RECOVERY') {
        // Do NOT clear isPasswordRecovery here — getAuthSession guard must stay active
        setStep('reset-password')
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem(IDLE_KEY)
        hasRoutedRef.current = false
        setUserId(null)
        setUserEmail(null)
        setActiveChildState(null)
        setChildren([])
        setCloudDataError(false)
        setStep('landing')
      }
    })

    // Check existing session on mount
    getAuthSession().then(async ({ user }) => {
      if (isPasswordRecovery.current) return // PASSWORD_RECOVERY event will handle routing
      if (user) {
        // If the SIGNED_IN handler already routed (fired before this resolves), skip.
        if (hasRoutedRef.current) return
        hasRoutedRef.current = true

        setUserId(user.id)
        setUserEmail(user.email ?? null)
        const preExistingChildren = getStoredChildren()

        if (preExistingChildren.length > 0) {
          // Cached data available — route immediately, sync cloud in background
          setChildren(preExistingChildren)
          const lastScreen = loadLastScreen() as { step: AppStep; dateKey: string } | null ?? undefined
          if (preExistingChildren.length === 1) {
            activateChild(preExistingChildren[0], user.id, lastScreen)
          } else {
            const savedActiveId = getActiveChildId()
            const savedActive = savedActiveId
              ? preExistingChildren.find((c) => c.id === savedActiveId)
              : null
            if (savedActive) {
              activateChild(savedActive, user.id, lastScreen)
            } else {
              setStep('child-list')
            }
          }
          // Refresh from cloud without blocking navigation
          loadAllUserData(user.id).then(({ error }) => {
            const refreshed = getStoredChildren()
            if (refreshed.length > 0) setChildren(refreshed)
            if (error && refreshed.length === 0) setCloudDataError(true)
          })
        } else {
          // No local data — must wait for cloud sync before routing
          setStep('loading')
          const { found: foundCloudData, error: cloudError } = await loadAllUserData(user.id)
          const storedChildren = getStoredChildren()
          setChildren(storedChildren)
          setCloudDataError(cloudError && storedChildren.length === 0)
          const lastScreen = loadLastScreen() as { step: AppStep; dateKey: string } | null ?? undefined
          if (!foundCloudData && preExistingChildren.length > 0) {
            // Local guest data exists but Supabase has none — offer import
            setGuestDataToImport(preExistingChildren)
            setChildren(preExistingChildren)
            setStep('child-list')
          } else if (storedChildren.length === 0) {
            setStep('add-child')
          } else if (storedChildren.length === 1) {
            activateChild(storedChildren[0], user.id, lastScreen)
          } else {
            const savedActiveId = getActiveChildId()
            const savedActive = savedActiveId
              ? storedChildren.find((c) => c.id === savedActiveId)
              : null
            if (savedActive) {
              activateChild(savedActive, user.id, lastScreen)
            } else {
              setStep('child-list')
            }
          }
        }
      } else {
        // Not authenticated — check for guest children
        // Do NOT set hasRoutedRef here: the user may log in later and we need
        // the SIGNED_IN event to route them.
        const storedChildren = getStoredChildren()
        setChildren(storedChildren)
        if (storedChildren.length === 0) {
          setStep('landing')
        } else {
          const lastScreen = loadLastScreen() as { step: AppStep; dateKey: string } | null ?? undefined
          // Has guest data — go straight to child list
          if (storedChildren.length === 1) {
            activateChild(storedChildren[0], null, lastScreen)
          } else {
            const savedActiveId = getActiveChildId()
            const savedActive = savedActiveId
              ? storedChildren.find((c) => c.id === savedActiveId)
              : null
            if (savedActive) {
              activateChild(savedActive, null, lastScreen)
            } else {
              setStep('child-list')
            }
          }
        }
      }
    })

    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Loading timeout: escape blank screen if auth init stalls (e.g. Chrome) ──
  // Only falls back to landing if auth has not started loading data — prevents
  // interrupting a slow loadAllUserData call with a premature redirect.
  useEffect(() => {
    const t = setTimeout(() => {
      setStep((s) => s === 'loading' ? 'landing' : s)
    }, 5000)
    return () => clearTimeout(t)
  }, [])

  // ── bfcache fix: Chrome restores pages from memory on back/forward nav ───────
  // Instead of force-reloading (which restarts auth init every time the user
  // navigates back), re-validate the session. Only sign out if the session
  // has expired while the page was cached.
  useEffect(() => {
    const handlePageShow = async (e: PageTransitionEvent) => {
      if (!e.persisted) return
      const { user } = await getAuthSession()
      if (!user && userId) {
        // Session expired while page was in bfcache — clear state and go to landing
        localStorage.removeItem(IDLE_KEY)
        hasRoutedRef.current = false
        setUserId(null)
        setUserEmail(null)
        setActiveChildState(null)
        setChildren([])
        setCloudDataError(false)
        setStep('landing')
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [userId])

  // ── Real-time cross-device sync ───────────────────────────────────────────

  useRealtimeSync(userId)

  // Keep refs in sync so the stable handler below always sees current values
  useEffect(() => { activeChildRef.current = activeChild }, [activeChild])
  useEffect(() => { selectedDateKeyRef.current = selectedDateKey }, [selectedDateKey])

  useEffect(() => {
    function onRemoteUpdate(e: Event) {
      const { table, childId, dateKey } = (e as CustomEvent<RemoteUpdateEvent>).detail
      if (table === 'children') {
        setChildren(getStoredChildren())
      } else if (table === 'training_sessions' && childId && dateKey) {
        if (activeChildRef.current?.id === childId && selectedDateKeyRef.current === dateKey) {
          const updated = getSessionsForDate(childId, dateKey)
          setSessionsState(updated.length > 0 ? updated : [createEmptySession('s-initial')])
        }
      }
    }
    window.addEventListener('energymatch:remote-update', onRemoteUpdate)
    return () => window.removeEventListener('energymatch:remote-update', onRemoteUpdate)
  }, [])

  // ── Data retention: prune localStorage entries older than 24 months ─────────

  useEffect(() => {
    if (children.length === 0) return
    children.forEach((c) => pruneOldLocalData(c.id))
  }, [children])

  // ── Midnight rollover: prompt user to plan the new day ───────────────────

  const LAST_SEEN_DATE_KEY = 'energymatch-last-seen-date'

  // Schedule a toast at midnight (handles the "still watching at midnight" case)
  useEffect(() => {
    if (!activeChild) return

    const midnight = new Date()
    midnight.setHours(24, 0, 0, 0)
    const msUntilMidnight = midnight.getTime() - Date.now()

    const timer = setTimeout(() => setShowNewDayToast(true), msUntilMidnight)
    return () => clearTimeout(timer)
  }, [activeChild])

  // Show toast on app open or tab refocus if the date has rolled over since last use.
  // Uses localStorage "last seen date" — reliable regardless of which date was selected.
  useEffect(() => {
    if (!activeChild) return

    const today = getTodayDateKey()
    const lastSeen = localStorage.getItem(LAST_SEEN_DATE_KEY) ?? today

    if (lastSeen < today) {
      setShowNewDayToast(true)
    }
    localStorage.setItem(LAST_SEEN_DATE_KEY, today)

    function checkOnFocus() {
      if (document.visibilityState !== 'visible') return
      const newToday = getTodayDateKey()
      const prevSeen = localStorage.getItem(LAST_SEEN_DATE_KEY) ?? newToday
      if (prevSeen < newToday) {
        setShowNewDayToast(true)
      }
      localStorage.setItem(LAST_SEEN_DATE_KEY, newToday)
    }

    document.addEventListener('visibilitychange', checkOnFocus)
    return () => document.removeEventListener('visibilitychange', checkOnFocus)
  }, [activeChild])

  // ── Idle session timeout (authenticated users only) ───────────────────────

  const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000
  const IDLE_KEY = 'em_last_active'

  useEffect(() => {
    if (!userId) return
    const updateActivity = () => localStorage.setItem(IDLE_KEY, String(Date.now()))
    const events = ['mousemove', 'keydown', 'touchstart', 'click', 'scroll'] as const
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }))
    // Only set the initial timestamp if not already present — preserves the value
    // across React StrictMode double-invocations and rapid userId changes, preventing
    // the next interval check from seeing a zero/missing timestamp and signing out.
    if (!localStorage.getItem(IDLE_KEY)) updateActivity()
    const interval = setInterval(() => {
      const last = Number(localStorage.getItem(IDLE_KEY))
      // Guard: only sign out if we have a valid timestamp (> 0) and it's stale enough.
      if (last > 0 && Date.now() - last > IDLE_TIMEOUT_MS) authSignOut()
    }, 60_000)
    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity))
      clearInterval(interval)
      // Do NOT remove IDLE_KEY here — the SIGNED_OUT handler clears it.
      // Removing it in effect cleanup (which runs on StrictMode remounts) would
      // cause the next interval to see a missing timestamp (= 0) and sign out immediately.
    }
  }, [userId])

  // ── Persist current screen ────────────────────────────────────────────────

  useEffect(() => {
    saveLastScreen(step, selectedDateKey)
  }, [step, selectedDateKey])

  // ── Child activation ─────────────────────────────────────────────────────

  function activateChild(
    child: ChildProfile,
    _uid: string | null,
    restore?: { step: AppStep; dateKey: string }
  ) {
    setActiveChildState(child)
    setActiveChildId(child.id)
    const dateKey = restore?.dateKey ?? getTodayDateKey()
    setSelectedDateKey(dateKey)
    const dateSessions = getSessionsForDate(child.id, dateKey)
    setSessionsState(
      dateSessions.length > 0 ? dateSessions : [createEmptySession('s-initial')]
    )
    setStep(restore?.step ?? 'training')
  }

  // ── Session management ────────────────────────────────────────────────────

  const setSessions = useCallback(
    (value: TrainingSession[] | ((prev: TrainingSession[]) => TrainingSession[])) => {
      setSessionsState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value
        if (activeChild) {
          setSessionsForDate(userId ?? '', activeChild.id, selectedDateKey, next)
        }
        return next
      })
    },
    [activeChild, userId, selectedDateKey]
  )

  const setSelectedDate = useCallback(
    (dateKey: string) => {
      setSelectedDateKey(dateKey)
      if (activeChild) {
        const dateSessions = getSessionsForDate(activeChild.id, dateKey)
        setSessionsState(
          dateSessions.length > 0 ? dateSessions : [createEmptySession('s-initial')]
        )
      }
    },
    [activeChild]
  )

  // ── Child CRUD ────────────────────────────────────────────────────────────

  function handleSaveChild(child: ChildProfile) {
    const existing = children.find((c) => c.id === child.id)
    let updatedChildren: ChildProfile[]
    if (existing) {
      updateStoredChild(child)
      updatedChildren = children.map((c) => (c.id === child.id ? child : c))
      setChildren(updatedChildren)
      if (userId) syncChild(userId, child)
      setActiveChildState(child)
      setStep('child-list')
    } else {
      addStoredChild(child)
      updatedChildren = [...children, child]
      setChildren(updatedChildren)
      if (userId) syncChild(userId, child)
      setStep('child-list')
    }
  }

  // ── Guest migration ───────────────────────────────────────────────────────

  function handleImportGuestData() {
    if (!guestDataToImport || !userId) return
    // All guest children are already in localStorage — just sync them to Supabase
    syncAllChildren(userId, guestDataToImport)
    // Clear legacy single-profile if present
    clearLegacyProfile()
    setGuestDataToImport(null)
    const refreshed = getStoredChildren()
    setChildren(refreshed)
    if (refreshed.length === 1) {
      activateChild(refreshed[0], userId)
    } else {
      setStep('child-list')
    }
  }

  function handleSkipImport() {
    setGuestDataToImport(null)
    const storedChildren = getStoredChildren()
    if (storedChildren.length === 0) {
      setStep('add-child')
    } else if (storedChildren.length === 1) {
      activateChild(storedChildren[0], userId)
    } else {
      setStep('child-list')
    }
  }

  // ── Navigation helpers ────────────────────────────────────────────────────

  const goToTraining = () => {
    setSessions((prev) => (prev.length === 0 ? [createEmptySession('s-initial')] : prev))
    setStep('training')
  }

  const goToPlanAnotherDay = () => {
    const today = getTodayDateKey()
    setSelectedDate(today)
    setStep('training')
  }

  const goToFuelPlan = () => setStep('fuel-plan')

  const goToMeals = (slotId?: string, suggestedMealName?: string) => {
    setLogFocusSlotId(slotId ?? null)
    setLogPrefill(
      slotId && suggestedMealName?.trim()
        ? { slotId, mealName: suggestedMealName.trim() }
        : null
    )
    setStep('meals')
  }

  const goToLogMealsForDate = useCallback(
    (dateKey?: string) => {
      if (dateKey) setSelectedDate(dateKey)
      setLogFocusSlotId(null)
      setLogPrefill(null)
      setStep('meals')
    },
    [setSelectedDate]
  )

  const goToFuelPlanForDate = useCallback(
    (dateKey?: string) => {
      if (dateKey) setSelectedDate(dateKey)
      setStep('fuel-plan')
    },
    [setSelectedDate]
  )

  const goToChildList = () => setStep('child-list')
  const goToFeedback = () => setStep('feedback')
  const goToDailyProgress = () => setStep('daily-progress')
  const goToCalendar = () => setStep('calendar')
  const goToAddChild = () => { setEditingChild(null); setStep('add-child') }

  // ── Screen rendering ──────────────────────────────────────────────────────

  if (step === 'loading') {
    return (
      <div className="logging-screen min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[var(--log-ink-10)] border-t-[var(--log-green-mid)] animate-spin" />
      </div>
    )
  }

  // Ensure activeChild is available for main app screens
  const child = activeChild

  let screen: ReactNode

  if (step === 'landing') {
    screen = (
      <Screen1
        onGetStartedGuest={() => {
          const storedChildren = getStoredChildren()
          setChildren(storedChildren)
          if (storedChildren.length === 0) {
            setStep('add-child')
          } else if (storedChildren.length === 1) {
            activateChild(storedChildren[0], null)
          } else {
            setStep('child-list')
          }
        }}
        onCreateAccount={() => setStep('signup')}
        onLogin={() => setStep('login')}
      />
    )
  } else if (step === 'login') {
    screen = (
      <ScreenLogin
        onBack={() => setStep('landing')}
        onSuccess={() => {
          // Reset the routing guard so the SIGNED_IN event from this explicit login
          // can always route, even if a previous session restore had set the guard.
          // This fixes the "stuck at login" issue when logging in on a second device.
          hasRoutedRef.current = false
          // Show loading state immediately so the user isn't left staring at
          // the login form while loadAllUserData runs in the background.
          setStep('loading')
        }}
        onForgotPassword={(email) => { setVerifyEmail(email); setStep('forgot-password') }}
      />
    )
  } else if (step === 'forgot-password') {
    screen = (
      <ScreenForgotPassword
        key={verifyEmail}
        initialEmail={verifyEmail}
        onBack={() => setStep('login')}
      />
    )
  } else if (step === 'reset-password') {
    screen = (
      <ScreenResetPassword
        onSuccess={() => {
          isPasswordRecovery.current = false
          authSignOut().then(() => setStep('login'))
        }}
      />
    )
  } else if (step === 'signup') {
    screen = (
      <ScreenSignUp
        onBack={() => setStep('landing')}
        onVerifyEmail={(email) => { setVerifyEmail(email); setStep('verify-email') }}
        onSuccess={() => {
          // Auth state change listener will handle routing (auto-confirm case)
        }}
      />
    )
  } else if (step === 'verify-email') {
    screen = (
      <ScreenVerifyEmail
        email={verifyEmail}
        onBack={() => setStep('signup')}
      />
    )
  } else if (step === 'account-settings') {
    screen = (
      <ScreenAccountSettings
        userEmail={userEmail ?? ''}
        userId={userId ?? ''}
        onBack={() => setStep('child-list')}
        onSignOut={() => setStep('landing')}
        onDeleted={() => setStep('landing')}
      />
    )
  } else if (step === 'child-list') {
    screen = (
      <ScreenChildList
        children={children}
        isAuthenticated={!!userId}
        userEmail={userEmail ?? undefined}
        onSelectChild={(c) => activateChild(c, userId)}
        onAddChild={goToAddChild}
        onEditChild={(c) => { setEditingChild(c); setStep('edit-child') }}
        onBack={() => setStep('landing')}
        onSignOut={() => setStep('landing')}
        onAccountSettings={userId ? () => setStep('account-settings') : undefined}
        guestDataToImport={guestDataToImport ?? undefined}
        onImportGuestData={handleImportGuestData}
        onSkipImport={handleSkipImport}
      />
    )
  } else if (step === 'add-child') {
    screen = (
      <Screen2
        mode="add"
        onSave={handleSaveChild}
        onBack={children.length === 0 ? () => setStep(userId ? 'child-list' : 'landing') : goToChildList}
      />
    )
  } else if (step === 'edit-child' && editingChild) {
    screen = (
      <Screen2
        mode="edit"
        initialProfile={editingChild}
        onSave={handleSaveChild}
        onBack={goToChildList}
      />
    )
  } else if (!child) {
    // No active child — go to child list
    screen = (
      <ScreenChildList
        children={children}
        isAuthenticated={!!userId}
        userEmail={userEmail ?? undefined}
        onSelectChild={(c) => activateChild(c, userId)}
        onAddChild={goToAddChild}
        onEditChild={(c) => { setEditingChild(c); setStep('edit-child') }}
        onSignOut={() => setStep('landing')}
        onAccountSettings={userId ? () => setStep('account-settings') : undefined}
      />
    )
  } else if (step === 'training') {
    screen = (
      <Screen3
        profile={child}
        sessions={sessions}
        setSessions={setSessions}
        dateKey={selectedDateKey}
        onDateChange={setSelectedDate}
        onNext={goToFuelPlan}
        onBack={goToChildList}
        onChangeChildProfile={goToChildList}
      />
    )
  } else if (step === 'fuel-plan') {
    screen = (
      <Screen4
        profile={child}
        sessions={sessions}
        dateKey={selectedDateKey}
        onDateChange={setSelectedDate}
        onBack={goToTraining}
        onStartOver={goToPlanAnotherDay}
        onChangeChildProfile={goToChildList}
        onLogMeals={goToMeals}
        onViewDailyProgress={goToDailyProgress}
        onViewCalendar={goToCalendar}
        onLogSessionFeedback={goToFeedback}
        childId={child.id}
      />
    )
  } else if (step === 'meals') {
    screen = (
      <Screen5
        profile={child}
        sessions={sessions}
        dateKey={selectedDateKey}
        onDateChange={setSelectedDate}
        onBack={goToFuelPlan}
        onStartOver={goToPlanAnotherDay}
        onChangeChildProfile={goToChildList}
        onSessionFeedback={goToFeedback}
        onViewDailyProgress={goToDailyProgress}
        onViewCalendar={goToCalendar}
        focusSlotId={logFocusSlotId}
        logPrefill={logPrefill}
        onClearLogPrefill={() => setLogPrefill(null)}
        pendingRecipe={pendingRecipe}
        onClearPendingRecipe={() => setPendingRecipe(null)}
        onViewRecipes={() => setStep('recipes')}
        userId={userId ?? ''}
        childId={child.id}
      />
    )
  } else if (step === 'feedback') {
    screen = (
      <Screen6
        profile={child}
        sessions={sessions}
        dateKey={selectedDateKey}
        onDateChange={setSelectedDate}
        onBack={() => setStep('meals')}
        onStartOver={goToPlanAnotherDay}
        onChangeChildProfile={goToChildList}
        onViewDailyProgress={goToDailyProgress}
        onViewCalendar={goToCalendar}
        onViewFuelPlan={goToFuelPlan}
        userId={userId ?? ''}
        childId={child.id}
      />
    )
  } else if (step === 'daily-progress') {
    screen = (
      <ScreenDailyProgress
        profile={child}
        sessions={sessions}
        dateKey={selectedDateKey}
        onDateChange={setSelectedDate}
        onBack={goToFuelPlan}
        onLogMeals={goToMeals}
        onStartOver={goToPlanAnotherDay}
        onChangeChildProfile={goToChildList}
        onViewCalendar={goToCalendar}
        onSessionFeedback={goToFeedback}
        childId={child.id}
      />
    )
  } else if (step === 'calendar') {
    screen = (
      <ScreenCalendar
        profile={child}
        onBack={goToFuelPlan}
        onLogMeals={goToLogMealsForDate}
        onViewFuelPlan={goToFuelPlanForDate}
        onStartOver={goToPlanAnotherDay}
        onChangeChildProfile={goToChildList}
        childId={child.id}
      />
    )
  } else if (step === 'recipes' && child) {
    screen = (
      <RecipesScreen
        childId={child.id}
        savedRecipes={getSavedRecipes(child.id)}
        supabaseUrl={import.meta.env.VITE_SUPABASE_URL as string ?? ''}
        anonKey={import.meta.env.VITE_SUPABASE_ANON_KEY as string ?? ''}
        authToken={authToken}
        onSaveRecipe={(name, entries, extras) => {
          const saved = saveRecipe(child.id, name, entries, extras)
          if (userId) syncRecipe(userId, child.id, saved)
        }}
        onDeleteRecipe={(id) => {
          deleteRecipe(child.id, id)
          if (userId) deleteRecipeSync(userId, id)
        }}
        onUpdateRecipe={(id, name, entries, extras) => {
          const updated = updateRecipe(child.id, id, name, entries, extras)
          if (updated && userId) syncRecipe(userId, child.id, updated)
        }}
        onUpdateRecipeTags={(id, tags) => {
          updateRecipeTags(child.id, id, tags)
          const updated = getSavedRecipes(child.id).find((r) => r.id === id)
          if (updated && userId) syncRecipe(userId, child.id, updated)
        }}
        onAddToLog={(recipe) => {
          setPendingRecipe(recipe)
          setStep('meals')
        }}
        onBack={() => setStep('meals')}
      />
    )
  } else if (step === 'not-found') {
    screen = <Screen404 onGoHome={() => setStep('landing')} />
  } else if (step === 'server-error') {
    screen = <Screen500 onGoHome={() => setStep('landing')} />
  } else {
    // Fallback
    screen = (
      <Screen4
        profile={child}
        sessions={sessions}
        dateKey={selectedDateKey}
        onDateChange={setSelectedDate}
        onBack={goToTraining}
        onStartOver={goToPlanAnotherDay}
        onChangeChildProfile={goToChildList}
        onLogMeals={goToMeals}
        onViewDailyProgress={goToDailyProgress}
        onViewCalendar={goToCalendar}
        onLogSessionFeedback={goToFeedback}
        childId={child.id}
      />
    )
  }

  return (
    <>
      <Suspense fallback={<div className="logging-screen min-h-screen" />}>
        {screen}
      </Suspense>
      {cloudDataError && (
        <div className="fixed bottom-20 left-0 right-0 z-[300] flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto max-w-sm w-full rounded-xl bg-red-50 border border-red-200 px-4 py-3 shadow-lg flex items-start gap-3">
            <span className="text-red-500 mt-0.5 shrink-0">⚠</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-red-800">Couldn't load your saved data</p>
              <p className="text-[12px] text-red-700 mt-0.5">Check your connection and log in again to retry.</p>
            </div>
            <button
              type="button"
              onClick={() => setCloudDataError(false)}
              className="text-red-400 hover:text-red-600 shrink-0 text-lg leading-none"
              aria-label="Dismiss"
            >×</button>
          </div>
        </div>
      )}
      {showNewDayToast && activeChild && (
        <div className="fixed bottom-20 left-0 right-0 z-[300] flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto max-w-sm w-full rounded-xl bg-[var(--log-green-soft,#f0fdf4)] border border-[var(--log-green-mid,#4ade80)] px-4 py-3 shadow-lg flex items-start gap-3">
            <span className="mt-0.5 shrink-0 text-base">🌅</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-green-900">It's a new day</p>
              <p className="text-[12px] text-green-800 mt-0.5">Plan today for {activeChild.firstName}?</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowNewDayToast(false)
                  const today = getTodayDateKey()
                  setSelectedDate(today)
                  setStep('training')
                }}
                className="text-[12px] font-semibold text-green-800 bg-green-100 hover:bg-green-200 rounded-lg px-2.5 py-1 transition-colors"
              >
                Plan today
              </button>
              <button
                type="button"
                onClick={() => setShowNewDayToast(false)}
                className="text-green-500 hover:text-green-700 text-lg leading-none"
                aria-label="Dismiss"
              >×</button>
            </div>
          </div>
        </div>
      )}
      <SyncStatusIndicator />
      <CookieConsent />
    </>
  )
}
